<template>
  <div :class="containerClass">

    <h2>{{ $route.params.id ? 'Edit Research' : 'Add Research' }}</h2>

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <b-form-group label="Research Title*" label-for="title" label-class="form-label">
        <b-form-input
          id="title"
          placeholder="Enter research title"
          v-model="title"
          :state="!$v.title.required && $v.title.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Institution*" label-for="institution" label-class="form-label">
        <b-form-input
          id="institution"
          placeholder="Enter institution"
          v-model="institution"
          :state="!$v.institution.required && $v.institution.$dirty ? false : null"
        />
        <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Faculty Name" label-for="faculty_name" label-class="form-label">
        <b-form-input id="faculty_name" placeholder="Enter faculty name" v-model="faculty_name" />
      </b-form-group>

      <b-form-group label="Role" label-for="role" label-class="form-label">
        <b-form-input id="role" placeholder="Enter your role" v-model="role" />
      </b-form-group>

      <b-form-group label="Description" label-for="description" label-class="form-label">
        <b-form-textarea
          id="description"
          placeholder="Describe your research"
          v-model="description"
          rows="3"
          max-rows="6"
        />
      </b-form-group>

      <b-form-group label="Start Date" label-for="start_date" class="form-label">
        <date-picker
          id="start_date"
          v-model="start_date"
          type="date"
          format="MM/DD/YYYY"
          value-type="YYYY-MM-DD"
          style="width: 100%"
          lang="en"
          placeholder="MM/DD/YYYY"
        >
        </date-picker>
      </b-form-group>

      <b-form-group label="End Date" label-for="end_date" class="form-label">
        <date-picker
          id="end_date"
          v-model="end_date"
          type="date"
          format="MM/DD/YYYY"
          value-type="YYYY-MM-DD"
          style="width: 100%"
          lang="en"
          placeholder="MM/DD/YYYY"
        >
        </date-picker>
      </b-form-group>

      <b-form-group label="Research URL" label-for="research_url" label-class="form-label">
        <b-form-input id="research_url" placeholder="https://..." v-model="research_url" />
      </b-form-group>

      <b-btn variant="primary" :disabled="isLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? $t('update') : 'Add' }}</span>
      </b-btn>
    </b-form>
  </div>
</template>

<script>
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Page from '@/components/Page.vue';
import { required } from 'vuelidate/lib/validators';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/locale/en';

export default {
  extends: Page,
  components: { DatePicker },

  data() {
    return {
      title: '',
      institution: '',
      faculty_name: '',
      role: '',
      description: '',
      start_date: null,
      end_date: null,
      research_url: '',
      isLoading: false,
    };
  },

  validations() {
    return {
      title: { required },
      institution: { required },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    isMobSmallScreen() {
      return this.windowWidth <= 420;
    },
  },

  methods: {
    ...mapActions('student', [
      'createResearch',
      'getResearch',
      'updateResearch',
      'updateShowEarnedPointToast',
    ]),
    get,
    goBack() {
      this.$router.go(-1);
    },
    async fetchResearch() {
      try {
        const res = await this.getResearch(this.$route.params.id);
        this.title = res.data.title;
        this.institution = res.data.institution;
        this.faculty_name = res.data.faculty_name;
        this.role = res.data.role;
        this.description = res.data.description;
        this.start_date = res.data.start_date;
        this.end_date = res.data.end_date;
        this.research_url = res.data.research_url;
      } catch (err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
    async onSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const data = {
            title: this.title,
            institution: this.institution,
            faculty_name: this.faculty_name || null,
            role: this.role || null,
            description: this.description || null,
            start_date: this.start_date,
            end_date: this.end_date,
            research_url: this.research_url || null,
          };

          if (this.$route.params.id) {
            await this.updateResearch({ id: this.$route.params.id, data });
            this.makeToast({ variant: 'success', msg: 'Research updated!' });
          } else {
            const researchResp = await this.createResearch(data);
            this.makeToast({ variant: 'success', msg: 'Research added!' });
            if (researchResp.data.data.points_earned > 0) {
              this.updateShowEarnedPointToast(researchResp.data.data.points_earned);
            }
          }
          setTimeout(() => {
            this.$router.push({ name: 'my-research' });
          }, 1000);
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
        this.isLoading = false;
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all required fields.' });
      }
    },
  },
  async mounted() {
    if (this.$route.params.id) {
      this.fetchResearch();
    }
  },
};
</script>

<style></style>
